import { bookingPackageEndDate } from './bookingPackageDates.js';
import { isBatchEndBeforeToday } from './customerBookingTab.js';

/** Booking extension / registration statuses that mean advance (or full) payment is recorded. */
const PAID_BOOKING_STATUSES = new Set([
  'advance_paid',
  'advanced_paid',
  'remaining_pending',
  'confirmed',
  'fully_paid'
]);

/**
 * True when the event batch is closed / inactive / already ended,
 * OR when the selected travelling package or stay option is closed for this batch.
 * Blocks online payment CTAs.
 */
export function bookingEventClosedForPayment(booking) {
  const batch = booking?.batch || booking?.registration?.event || null;
  if (!batch) return false;
  if (batch.isActive === false) return true;
  const status = String(batch.status || '').trim().toLowerCase();
  if (status && ['closed', 'cancelled', 'canceled', 'inactive', 'completed'].includes(status)) {
    return true;
  }
  const end = bookingPackageEndDate(booking) || batch.endDate;
  if (isBatchEndBeforeToday(end)) return true;

  // Check package & stay availability
  const availability =
    batch.availabilityMeta ||
    batch.packageAvailability ||
    batch.package_availability ||
    null;

  if (availability && typeof availability === 'object') {
    // 1. Check travel package
    const pkgId =
      booking?.joinFromPackageId ??
      booking?.registration?.packageId ??
      booking?.joinFromPackage?.id ??
      booking?.registration?.package?.id ??
      null;

    if (pkgId != null && availability.packages) {
      const pkgKey = String(pkgId);
      const pkgMeta = availability.packages[pkgKey];
      if (pkgMeta && (pkgMeta.enabled === false || pkgMeta.isFull === true)) {
        return true;
      }
    }

    // 2. Check stay option
    const stayOpt = booking?.stayOption;
    if (stayOpt && availability.stayOptions) {
      const stayKey = String(stayOpt);
      const stayMeta = availability.stayOptions[stayKey];
      if (stayMeta && (stayMeta.enabled === false || stayMeta.isFull === true)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * True when the user has completed at least the booking advance (or full payment).
 * Used to unlock WhatsApp group link and cancellation request on the dashboard.
 */
export function bookingHasRecordedPayment(booking) {
  if (!booking) return false;

  const status = String(booking.status || '').trim().toLowerCase();
  const regStatus = String(booking.registration?.status || '').trim().toLowerCase();
  if (PAID_BOOKING_STATUSES.has(status) || PAID_BOOKING_STATUSES.has(regStatus)) {
    return true;
  }

  const paid =
    Number(booking.amountPaidAtOffice ?? 0) + Number(booking.amountPaidOnline ?? 0);
  const advance = Number(booking.advanceAmount ?? 0);
  if (!Number.isFinite(paid) || paid <= 0) return false;
  if (advance > 0) return paid >= advance;
  return true;
}

const BLOCKING_CANCELLATION_STATUSES = new Set(['pending', 'cancelled', 'refunded']);

function normalizeCancellationStatus(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  if (raw === 'canceled') return 'cancelled';
  return raw;
}

/** False when booking is cancelled or a cancellation request already exists. */
export function userCanRequestCancellation(booking) {
  if (!booking?.id) return false;
  const bookingStatus = String(booking.status || '').trim().toLowerCase();
  if (bookingStatus === 'cancelled') return false;
  if (booking.pendingCancellationRequest) return false;
  const cancelStatus = normalizeCancellationStatus(booking.cancellationRequestStatus);
  if (BLOCKING_CANCELLATION_STATUSES.has(cancelStatus)) return false;
  return bookingHasRecordedPayment(booking);
}

/** Balance still owed on trip fee (total minus office + online paid). */
export function bookingRemainingDueAmount(booking) {
  if (!booking) return 0;
  const paid =
    Number(booking.amountPaidAtOffice ?? 0) + Number(booking.amountPaidOnline ?? 0);
  const total = Number(booking.totalAmount ?? 0);
  if (!Number.isFinite(total) || total <= 0) return 0;
  return Math.max(0, total - paid);
}

/** Amount still payable now for initial booking stage (advance or full if no advance split). */
export function bookingPayNowDueAmount(booking) {
  if (!booking) return 0;
  const paid =
    Number(booking.amountPaidAtOffice ?? 0) + Number(booking.amountPaidOnline ?? 0);
  const total = Number(booking.totalAmount ?? 0);
  const advance = Number(booking.advanceAmount ?? 0);
  if (!Number.isFinite(total) || total <= 0) return 0;
  const payableNow = advance > 0 ? Math.min(advance, total) : total;
  return Math.max(0, payableNow - paid);
}

/**
 * Show "Pay remaining" when advance (or partial) is recorded and balance is still due.
 */
export function userCanPayRemainingOnline(booking) {
  if (!booking?.id) return false;
  if (bookingEventClosedForPayment(booking)) return false;
  const status = String(booking.status || '').trim().toLowerCase();
  const regStatus = String(booking.registration?.status || '').trim().toLowerCase();
  if (status === 'cancelled' || status === 'fully_paid') return false;
  if (booking.pendingCancellationRequest) return false;
  const cancelStatus = normalizeCancellationStatus(booking.cancellationRequestStatus);
  if (BLOCKING_CANCELLATION_STATUSES.has(cancelStatus)) return false;

  const remaining = bookingRemainingDueAmount(booking);
  if (remaining <= 0.02) return false;

  if (bookingHasRecordedPayment(booking)) return true;
  if (status === 'confirmed' || regStatus === 'confirmed' || regStatus === 'advanced_paid') return true;
  return false;
}

/** Show initial online payment CTA (advance/full) before payment is recorded. */
export function userCanPayNowOnline(booking) {
  if (!booking?.id) return false;
  if (bookingEventClosedForPayment(booking)) return false;
  const status = String(booking.status || '').trim().toLowerCase();
  if (status === 'cancelled' || status === 'fully_paid' || status === 'confirmed') return false;
  if (booking.pendingCancellationRequest) return false;
  const cancelStatus = normalizeCancellationStatus(booking.cancellationRequestStatus);
  if (BLOCKING_CANCELLATION_STATUSES.has(cancelStatus)) return false;
  if (bookingHasRecordedPayment(booking)) return false;
  return bookingPayNowDueAmount(booking) > 0.02;
}
